import React from "react";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Link from "../Link";
import CertificateIcon from "../../shared/icons/Certificate";
import ClimateIcon from "../../shared/icons/Climate";
import EuroIcon from "../../shared/icons/Euro";
import FundingIcon from "../../shared/icons/Funding";
import ScrollIcon from "../../shared/icons/Scroll";
import Inspektionsstellen from "./Inspektionsstellen";
import Probenehmer from "./Probenehmer";
import Prueflabore from "./Prueflabore";
import Hackschnitzelrechner from "../Hackschnitzelrechner";
import Zertifizierungsrechner from "../Zertifizierungsrechner";
import Foerderrechner from "../Foerderrechner";

export const CONTEXT_LABELS = {
  holzbriketts: "Briketts",
  pellets: "Pellets",
  hackschnitzel: "Hackschnitzel",
};

export const NAV_HEIGHT = 48;
export const LOGOS = {
  pellets: "/enplus/logos/pellets.svg",
  holzbriketts: "/enplus/logos/holzbriketts.svg",
  hackschnitzel: "/enplus/logos/hackschnitzel.svg",
};

export const HEADER_IMAGES = (context, wide = false) => {
  return {
    pellets: [`/enplus/header/pellets01.jpg`],
    holzbriketts: [
      `/enplus/header${wide ? "/wide" : ""}/briketts01.jpg`,
      `/enplus/header${wide ? "/wide" : ""}/briketts02.jpg`,
      `/enplus/header${wide ? "/wide" : ""}/briketts03.jpg`,
      `/enplus/header${wide ? "/wide" : ""}/briketts04.jpg`,
      `/enplus/header${wide ? "/wide" : ""}/briketts05.jpg`,
    ],
    hackschnitzel: [`/enplus/header${wide ? "/wide" : ""}/hackschnitzel01.jpg`],
  }[context];
};

export const CLAIMS = {
  pellets: "Das Qualitätszertifikat für Holzpellets",
  holzbriketts: "Zertifizierte Briketts mit gesicherter Qualität",
  hackschnitzel: "Mit zertifizierten Hackschnitzeln sicher heizen",
};

const createLink = (context, link) => {
  const linkContext =
    process.env.NODE_ENV === "production" ? "/" : `/enplus/${context}/`;
  return `${linkContext}${link}`;
};

export const STATS_TEXT = {
  pellets: [
    <React.Fragment>
      Zertifizierung EN<i>plus</i>-Pellets
    </React.Fragment>,
    <React.Fragment>
      EN<i>plus</i>-zertifizierte Pellets sind ein moderner erneuerbarer
      Energieträger. Mit ihren streng definierten Eigenschaften, die über die
      geltende Norm hinausgehen, heizen Sie komfortabel und effizient. EN
      <i>plus</i> überwacht die gesamte Bereitstellungskette von der Herstellung
      über den Handel bis zur Anlieferung beim Kunden, um die hohe
      Brennstoffqualität bis in den Lagerraum zu sichern. <br />{" "}
      <Link
        underline
        variant="h6"
        color="primary"
        href={createLink("pellets", "vorteile")}
      >
        Zu den Vorteilen
      </Link>
    </React.Fragment>,
  ],
  holzbriketts: [
    <React.Fragment>
      Zertifizierung EN<i>plus</i>-Briketts
    </React.Fragment>,
    <React.Fragment>
      Qualitativ hochwertige Brennstoffe sind die Voraussetzung für moderne
      Holzenergie. Ziel von EN<i>plus</i>-Briketts ist ein gleichbleibender
      Brennstoff, der den strengen Anforderungen der Luftreinhaltung in
      Deutschland (gemäß 1. BImSchV) entspricht und dabei störungsfrei genutzt
      werden kann.
    </React.Fragment>,
  ],
  hackschnitzel: [
    <React.Fragment>
      Zertifizierung EN<i>plus</i>-Hackschnitzel
    </React.Fragment>,
    <React.Fragment>
      Qualitativ hochwertige Brennstoffe sind die Voraussetzung für moderne
      Holzenergie: Strenge Anforderungen an Luftreinhaltung und
      Ressourceneffizienz werden erfüllt und mit Komfort, Klimaschutz und
      Wirtschaftlichkeit verbunden. EN<i>plus</i>-zertifizierte Hackschnitzel
      garantieren gleichbleibend homogene Brennstoffeigenschaften. Diese sind
      entscheidend für einen reibungslosen und effizienten Anlagenbetrieb.
    </React.Fragment>,
  ],
};

export const BENEFITS = {
  holzbriketts: [
    <React.Fragment>
      Vorteile von EN<i>plus</i>-Briketts
    </React.Fragment>,
    [
      "Homogener Brennstoff mit geringen Emissionen",
      "Aus naturbelassenen Sägespänen mit einem Wassergehalt von unter 15 Prozent",
      "Hoher Heizwert von mind. 4,25 kWh/kg, höhere Energiedichte als Scheitholz",
      "Leichte Handhabung durch Anlieferung auf Paletten",
    ],
  ],
  hackschnitzel: [
    <React.Fragment>
      Vorteile von EN<i>plus</i>-Hackschnitzeln
    </React.Fragment>,
    [
      "Standardisierte Brennstoffeigenschaften",
      "Gesicherte Qualität",
      "Störungsarmer Anlagenbetrieb",
      "Effiziente Verbrennung",
      "Geringe Emissionen",
      "Transparente Abrechnung",
      "Geregeltes Beschwerdemanagement",
    ],
  ],
};

export const LINKS = {
  pellets: [
    {
      key: "enplus-pellets",
      label: (
        <b>
          EN<i>plus</i>-Pellets
        </b>
      ),
      sections: [
        [
          {
            label: "Kontakt",
            link: "kontakt",
            hideInNav: true,
          },
          {
            label: "Impressum",
            uuid: "af1ddd62-524f-40a1-82a0-14f280a3e052",
            link: "impressum",
            hideInNav: true,
          },
          {
            label: "Datenschutz",
            uuid: "db535017-8303-4f3c-850a-8cac8e6338fc",
            link: "datenschutz",
            hideInNav: true,
          },
          {
            label: "Vorteile",
            uuid: "313f7a0b-4c6b-447a-8551-65a2a0beb002",
            link: "vorteile",
          },
          {
            label: "Eigenschaften",
            uuid: "1360f3bb-5862-4458-989a-0dd6807ae3a9",
            link: "eigenschaften",
          },
          {
            label: "Historie",
            uuid: "153e18f0-032f-40b4-abb2-bb81beb67e5a",
            link: "historie",
          },
        ],
        [
          {
            label: "Ausschreibungen",
            uuid: "7c19e2fd-ca10-4e44-bac5-78967670f80e",
            link: "ausschreibungen",
          },
          {
            label: "Ausschreibungsvorlage",
            uuid: "ba241762-62e4-4a46-af71-bdc0956e056b",
            link: "ausschreibungsvorlage",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Aktuelle Ausschreibungen",
            link: "aktuelle-ausschreibungen",
            variant: "subtitle1",
            color: "textSecondary",
          },
        ],
        [
          {
            label: "Organisation",
            uuid: "8a3c55f8-6420-4023-8ba2-2925732c5447",
            link: "organisation",
          },
          {
            label: "Management und Zertifizierungsstelle",
            uuid: "808a2bc5-df49-4621-835b-b5b6daf0d179",
            link: "managment-und-zertifizierungsstelle",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Inspektionsstellen",
            uuid: "52197710-58ea-45f7-9440-63c66aa8c052",
            link: "inspektionsstellen",
            variant: "subtitle1",
            color: "textSecondary",
            children: <Inspektionsstellen />,
            layoutProps: {
              darkFooter: true,
            },
          },
          {
            label: "Prüflabore",
            uuid: "088c878f-c0cc-4818-bc2a-e417cbc8cafd",
            link: "prueflabore",
            variant: "subtitle1",
            color: "textSecondary",
            children: <Prueflabore />,
            layoutProps: {
              darkFooter: true,
            },
          },
        ],
        [
          {
            label: (
              <React.Fragment>
                Das EN<i>plus</i>-Zeichen
              </React.Fragment>
            ),
            uuid: "c0af1708-f0f2-4982-b7c5-228817646d53",
            link: "das-enplus-zeichen",
          },
          {
            label: "FAQ",
            uuid: "defd882c-0f4b-4908-9612-c9ed19d5845a",
            link: "faq",
          },
        ],
      ],
    },
    {
      key: "zertifizierte-unternehmen",
      label: <b>Zertifizierte Unternehmen</b>,
      link: "unternehmen-finden",
    },
    {
      key: "zertifizierung",
      label: <b>Zertifizierung</b>,
      sections: [
        [
          {
            label: "Für Produzenten",
            uuid: "6acc76d6-2e16-4cb5-a348-100d562961bc",
            link: "zertifizierung/fuer-produzenten",
          },
          {
            label: "Weg zum Zertifikat",
            uuid: "af3b8511-50ef-4174-a56e-4574b0b612be",
            link: "zertifizierung/fuer-produzenten/weg-zum-zertifikat",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Anforderungen & Qualitätssicherung",
            uuid: "df38ec24-6aad-43bb-a98f-16d7f6d9c200",
            link:
              "zertifizierung/fuer-produzenten/anforderungen-qualitaetssicherung",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Entgeltordnung",
            uuid: "3b47d171-e1e1-4d8c-ada4-c2a5fcd2b40b",
            link: "zertifizierung/fuer-produzenten/entgeltordnung",
            variant: "subtitle1",
            color: "textSecondary",
          },
        ],
        [
          {
            label: "Für Händler",
            uuid: "171dc263-e260-420f-9100-cab2460df013",
            link: "zertifizierung/fuer-haendler",
          },
          {
            label: "Weg zum Zertifikat",
            uuid: "850c8f59-b85e-41b0-9684-677391cd6ba6",
            link: "zertifizierung/fuer-haendler/weg-zum-zertifikat",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Anforderungen & Qualitätssicherung",
            uuid: "1b18d9c9-86dc-4e16-ad1b-1751a07880e8",
            link:
              "zertifizierung/fuer-haendler/anforderungen-qualitaetssicherung",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Entgeltordnung",
            uuid: "8f3be48d-c81f-4f34-b56f-fb4c15b7d8ba",
            link: "zertifizierung/fuer-haendler/entgeltordnung",
            variant: "subtitle1",
            color: "textSecondary",
          },
        ],
        [
          {
            label: "Für Dienstleistungsanbieter",
            uuid: "71029654-349d-4d7e-8646-0748c0d8112f",
            link: "zertifizierung/fuer-dienstleistungsanbieter",
          },
          {
            label: "Weg zum Zertifikat",
            uuid: "6f584dd8-cee5-49c6-be87-6db7b0946193",
            link:
              "zertifizierung/fuer-dienstleistungsanbieter/weg-zum-zertifikat",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Anforderungen an die Logistik & Qualitätssicherung",
            uuid: "88b4f588-c597-4a2e-9f28-2327381e022d",
            link:
              "zertifizierung/fuer-dienstleistungsanbieter/anforderungen-qualitaetssicherung",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Entgeltordnung",
            uuid: "39c3083f-f63e-4b63-bf76-850aa3077a18",
            link: "zertifizierung/fuer-dienstleistungsanbieter/entgeltordnung",
            variant: "subtitle1",
            color: "textSecondary",
          },
        ],
        [
          {
            label: (
              <>
                EN<i>plus</i>-Handbuch und Zertifizierungsdokumente
              </>
            ),
            uuid: "2ef85968-d6bf-43fe-aa82-1d1476ccf9ca",
            link: "zertifizierung/handbuch",
          },
        ],
      ],
    },
    {
      key: "enplus-zeichen",
      label: (
        <b>
          EN<i>plus</i>-Zeichen
        </b>
      ),
      sections: [
        [],
        [
          {
            label: (
              <>
                EN<i>plus</i>-Markenzeichen
              </>
            ),
            uuid: "e9549917-7a2d-4525-8ec9-4f8dabfff7fb",
            link: "enplus-zeichen/unerlaubte-nutzung",
          },
        ],
        [],
      ],
    },
    {
      key: "mediathek",
      label: <b>Mediathek & News</b>,
      sections: [
        [
          {
            label: "Mediathek",
            link: "mediathek",
          },
          {
            label: "News und Pressemitteilungen",
            link: "news",
          },
        ],
        [
          {
            label: "Pressebilder",
            link: "pressebilder",
          },
          {
            label: "Termine",
            link: "termine",
          },
        ],
        [
          {
            label: "DEPI-Shop",
            href: "http://shop.depi.de/",
            target: "_blank",
            rel: "noopener noreferer",
          },
        ],
      ],
    },
  ],
  holzbriketts: [
    {
      key: "enplus-briketts",
      label: (
        <b>
          EN<i>plus</i>-Briketts
        </b>
      ),
      sections: [
        [
          {
            label: "Kontakt",
            link: "kontakt",
            hideInNav: true,
          },
          {
            label: "Impressum",
            uuid: "af1ddd62-524f-40a1-82a0-14f280a3e052",
            link: "impressum",
            hideInNav: true,
          },
          {
            label: "Datenschutz",
            uuid: "db535017-8303-4f3c-850a-8cac8e6338fc",
            link: "datenschutz",
            hideInNav: true,
          },
          {
            label: "Vorteile der Zertifizierung",
            uuid: "4bbd1c7c-d7da-4617-a725-563d362a4f11",
            link: "vorteile-der-zertifizierung",
          },
          {
            label: "Eigenschaften",
            uuid: "8d1d55e9-6cd7-4304-a5b7-b4eee0f1453f",
            link: "eigenschaften",
          },
        ],
        [
          {
            label: "Gesicherte Qualität",
            uuid: "4bd3704c-c3ed-419e-9015-b384b8e43167",
            link: "gesicherte-qualitaet",
          },
          {
            label: (
              <React.Fragment>
                Das EN<i>plus</i>-Zeichen
              </React.Fragment>
            ),
            uuid: "769fbbab-db9e-45fb-9651-19ef1f6702e1",
            link: "das-enplus-zeichen",
          },
        ],
        [
          {
            label: "Organisation",
            uuid: "a86ab93f-52d9-478a-b93a-20788bc5a019",
            link: "organisation",
            children: <Probenehmer />,
            layoutProps: {
              darkFooter: true,
            },
          },
          {
            label: "FAQ",
            uuid: "7647c5d9-06c2-4476-bb4e-392e3ece81e1",
            link: "faq",
          },
        ],
      ],
    },
    {
      key: "zertifizierte-unternehmen",
      label: <b>Zertifizierte Unternehmen</b>,
      link: "unternehmen-finden",
    },
    {
      key: "zertifizierung",
      label: <b>Zertifizierung</b>,
      sections: [
        [
          {
            label: "Weg zum Zertifikat",
            uuid: "2d721214-9c82-4f4c-8ac2-96525454d941",
            link: "zertifizierung/der-weg-zum-zertifikat",
          },
        ],
        [
          {
            label: "Anforderungen & Qualitätssicherung",
            uuid: "ef9832ca-faa2-4968-bfd9-a21e8522d254",
            link: "zertifizierung/anforderungen-qualitaetssicherung",
          },
          {
            label: "Entgeltordnung",
            uuid: "b9de99c1-2663-4394-9e80-2f0e05737b0b",
            link: "zertifizierung/entgeltordnung",
          },
        ],
        [
          {
            label: (
              <>
                EN<i>plus</i>-Handbuch und Zertifizierungsdokumente
              </>
            ),
            uuid: "64ecd648-0ebf-4075-ab96-be698d66a1bd",
            link: "zertifizierung/handbuch",
          },
        ],
      ],
    },
    {
      key: "heizen-mit-briketts",
      label: <b>Heizen mit Briketts</b>,
      sections: [
        [
          {
            label: (
              <React.Fragment>
                Vorteile von EN<i>plus</i>-Briketts
              </React.Fragment>
            ),
            uuid: "4996b9e6-74ba-45f0-bbe6-cd0d3b13b296",
            link: "heizen-mit-briketts/vorteile",
          },
          {
            label: (
              <React.Fragment>
                Kennzeichnung von EN<i>plus</i>-Briketts
              </React.Fragment>
            ),
            uuid: "3ee5d620-5530-4c9c-831d-859b0c6d644c",
            link: "heizen-mit-briketts/kennzeichnung",
          },
        ],
        [
          {
            label: "Holzbriketts und deren Herstellung",
            uuid: "9aa4b21a-d1e1-4838-b389-384bd60a9495",
            link: "heizen-mit-briketts/herstellung",
          },
        ],
        [
          {
            label: "Richtiges Heizen",
            uuid: "a057749b-468c-48e9-a141-218f292bb0bb",
            link: "heizen-mit-briketts/richtiges-heizen",
          },
          // {
          //   label: 'Lagerung',
          //   uuid: 'cd2cdfae-034a-4916-a806-1f8733f8ee67',
          //   link: 'heizen-mit-briketts/lagerung',
          //   variant: 'subtitle1',
          //   color: 'textSecondary',
          // },
          // {
          //   label: 'Anzünden und Nachlegen',
          //   uuid: 'd55ab4f9-aa0e-47fb-b323-26e8d94edfae',
          //   link: 'heizen-mit-briketts/anzuenden-und-nachlegen',
          //   variant: 'subtitle1',
          //   color: 'textSecondary',
          // },
          // {
          //   label: 'Feuerungseinstellungen anpassen',
          //   uuid: 'b62b530d-d47e-4b67-8cf0-4ccc2db6b8af',
          //   link: 'heizen-mit-briketts/euerungseinstellungen-anpassen',
          //   variant: 'subtitle1',
          //   color: 'textSecondary',
          // },
        ],
      ],
    },
    {
      key: "enplus-zeichen",
      label: (
        <b>
          EN<i>plus</i>-Zeichen
        </b>
      ),
      sections: [
        [],
        [
          {
            label: (
              <>
                EN<i>plus</i>-Markenzeichen
              </>
            ),
            uuid: "e9549917-7a2d-4525-8ec9-4f8dabfff7fb",
            link: "enplus-zeichen/unerlaubte-nutzung",
          },
        ],
        [],
      ],
    },
    {
      key: "mediathek",
      label: <b>Mediathek & News</b>,
      sections: [
        [
          {
            label: "Mediathek",
            link: "mediathek",
          },
          {
            label: "News und Pressemitteilungen",
            link: "news",
          },
        ],
        [
          {
            label: "Pressebilder",
            link: "pressebilder",
          },
          {
            label: "Termine",
            link: "termine",
          },
        ],
        [
          {
            label: "DEPI-Shop",
            href: "http://shop.depi.de/",
            target: "_blank",
            rel: "noopener noreferer",
          },
        ],
      ],
    },
  ],
  hackschnitzel: [
    {
      key: "enplus-hackschnitzel",
      label: (
        <b>
          EN<i>plus</i>-Hackschnitzel
        </b>
      ),
      sections: [
        [
          {
            label: "Kontakt",
            link: "kontakt",
            hideInNav: true,
          },
          {
            label: "Impressum",
            uuid: "af1ddd62-524f-40a1-82a0-14f280a3e052",
            link: "impressum",
            hideInNav: true,
          },
          {
            label: "Datenschutz",
            uuid: "db535017-8303-4f3c-850a-8cac8e6338fc",
            link: "datenschutz",
            hideInNav: true,
          },
          {
            label: "Vorteile",
            uuid: "d074748f-a7c9-4676-8c4f-9b0d19f989fc",
            link: "vorteile",
          },
          {
            label: "Zertifizierte Ware erkennen",
            uuid: "f9251203-b768-4e72-9c2c-5840ffb8968f",
            link: "zertifizierte-ware-erkennen",
          },
          {
            label: "Förderprogramme",
            uuid: "4e277423-505a-427f-b203-8a14c669648f",
            link: "foerderprogramme",
            children: <Foerderrechner />,
          },
        ],
        [
          {
            label: "Ausschreibungen",
            uuid: "7271b1d3-e6d0-48f6-ad23-e01653fa5cc7",
            link: "ausschreibungen",
          },
          {
            label: "Ausschreibungsvorlage",
            uuid: "aa6b06bd-71f7-4f84-985a-3365595d97e4",
            link: "ausschreibungsvorlage",
            variant: "subtitle1",
            color: "textSecondary",
          },
          {
            label: "Aktuelle Ausschreibungen",
            variant: "subtitle1",
            color: "textSecondary",
            link: "aktuelle-ausschreibungen",
          },
        ],
        [
          {
            label: "Einsatz von Hackschnitzeln",
            uuid: "a64e79f7-c127-4563-ab07-1df24dd03d9c",
            link: "einsatz-von-hackschnitzeln",
            children: <Hackschnitzelrechner />,
          },
          {
            label: "FAQ",
            uuid: "11e4386b-543d-4bc6-9e0b-55ecf930719b",
            link: "faq",
          },
        ],
      ],
    },
    {
      key: "zertifizierte-unternehmen",
      label: <b>Zertifizierte Unternehmen</b>,
      link: "unternehmen-finden",
    },
    {
      key: "zertifizierung",
      label: <b>Zertifizierung</b>,
      sections: [
        [
          {
            label: "Aufbau des Zertifizierungsprogramms",
            uuid: "14f273ac-b538-4cf2-aca0-f363d158929c",
            link: "zertifizierung/aufbau-des-zertifizierungsprogramms",
          },
          {
            label: (
              <>
                EN<i>plus</i>-Handbuch und Zertifizierungsdokumente
              </>
            ),
            uuid: "f540be64-528c-4daf-a85d-32a688addc00",
            link: "zertifizierung/handbuch",
          },
        ],
        [
          {
            label: "Weg zum Zertifikat",
            uuid: "27ea98ed-d1eb-4b42-80c7-2033bb1fd056",
            link: "zertifizierung/der-weg-zum-zertifikat",
          },
          {
            label: "Zertifizierungsgebühren",
            uuid: "7cf6c5fc-f911-4154-ab14-ccf6f845c546",
            link:
              "zertifizierung/entgeltordnung-und-rechner-zertifizierungsgebuehren",
            children: <Zertifizierungsrechner />,
          },
        ],
        [
          {
            label: "Historie: Projekt HackZert",
            uuid: "08164872-cc0f-48d5-8c69-bfe93e2c3e8a",
            link: "zertifizierung/historie-projekt-hackzert",
          },
        ],
      ],
    },
    {
      key: "heizen-mit-hackschnitzeln",
      label: <b>Heizen mit Hackschnitzeln</b>,
      sections: [
        [
          {
            label: "Hackschnitzelpreis",
            uuid: "1622b2bc-85fc-4f7a-b3df-7da7a1cc7828",
            link: "heizen-mit-hackschnitzeln/preis",
            children: <Hackschnitzelrechner />,
          },
          {
            label: "Technische und rechtliche Anforderungen",
            uuid: "c000161a-185b-4697-acdc-96945c3fa4bf",
            link:
              "heizen-mit-hackschnitzeln/technische-und-rechtliche-anforderungen",
          },
        ],
        [
          {
            label: "Hackschnitzelproduktion",
            uuid: "b3fd11e8-1f81-40f3-8581-746b930f9c5a",
            link: "heizen-mit-hackschnitzeln/produktion",
          },
          {
            label: "Hackschnitzelheizsysteme",
            uuid: "515f2c6b-610c-4b22-ae99-da15a81034a9",
            link: "heizen-mit-hackschnitzeln/heizsysteme",
          },
        ],
        [
          {
            label: "Hackschnitzel lagern",
            uuid: "412834d8-4285-45e2-82f9-59e79e36e1d4",
            link: "heizen-mit-hackschnitzeln/lagerung",
          },
          {
            label: "Klima und Umwelt",
            uuid: "bd7edb13-1f8e-4a69-bc5b-14aadfb60ffd",
            link: "heizen-mit-hackschnitzeln/klima-und-umwelt",
          },
        ],
      ],
    },
    {
      key: "enplus-zeichen",
      label: (
        <b>
          EN<i>plus</i>-Zeichen
        </b>
      ),
      sections: [
        [],
        [
          {
            label: (
              <>
                EN<i>plus</i>-Markenzeichen
              </>
            ),
            uuid: "e9549917-7a2d-4525-8ec9-4f8dabfff7fb",
            link: "enplus-zeichen/unerlaubte-nutzung",
          },
        ],
        [],
      ],
    },
    {
      key: "mediathek",
      label: <b>Mediathek & News</b>,
      sections: [
        [
          {
            label: "Mediathek",
            link: "mediathek",
          },
          {
            label: "News  und Pressemitteilungen",
            link: "news",
          },
        ],
        [
          {
            label: "Pressebilder",
            link: "pressebilder",
          },
          {
            label: "Termine",
            link: "termine",
          },
        ],
        [
          {
            label: "DEPI-Shop",
            href: "http://shop.depi.de/",
            target: "_blank",
            rel: "noopener noreferer",
          },
        ],
      ],
    },
  ],
};

export const CERTIFICATE_LINKS = {
  pellets: [
    {
      label: "Für Händler",
      link: "zertifizierung/fuer-haendler",
      onClick: () => {},
    },
    {
      label: "Für Produzenten",
      link: "zertifizierung/fuer-produzenten",
      onClick: () => {},
    },
    {
      label: "Für Dienstleistungsanbieter",
      link: "zertifizierung/fuer-dienstleistungsanbieter",
      onClick: () => {},
    },
  ],
  holzbriketts: [{ link: "zertifizierung/der-weg-zum-zertifikat" }],
  hackschnitzel: [{ link: "zertifizierung/der-weg-zum-zertifikat" }],
};

const useStyles = makeStyles((theme) => ({
  themeLink: {
    cursor: "default",
    textDecoration: "underline",
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
}));

const ThemeLink = ({ context, href, as, ...props }) => {
  const classes = useStyles(props);
  const linkContext =
    process.env.NODE_ENV === "production" ? "/" : `/enplus/${context}/`;
  return (
    <Link
      color="primary"
      className={classes.themeLink}
      href={`${linkContext}${href}`}
      as={`${linkContext}${as}`}
      {...props}
    />
  );
};
const ThemeParagraph = (props) => (
  <Typography variant="h6" component="p" {...props} />
);

export const THEMES = {
  hackschnitzel: [
    {
      title: (
        <React.Fragment>
          Zertifizierung EN<i>plus</i>
        </React.Fragment>
      ),
      icon: CertificateIcon,
      content: (
        <React.Fragment>
          <ThemeParagraph>
            Ziel der Zertifizierung EN<i>plus</i>-Hackschnitzel ist die
            gesicherte Bereitstellung eines qualitativ hochwertigen Brennstoffs,
            der den strengen Anforderungen der Luftreinhaltung in Deutschland
            (gemäß 1. BImSchV) entspricht. Gleichzeitig wird dabei ein
            reibungsloser Heizungsbetrieb gewährleistet.
          </ThemeParagraph>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="zertifizierung/aufbau-des-zertifizierungsprogramms"
          >
            Aufbau des Zertifizierungsprogramms
          </ThemeLink>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="zertifizierung/entgeltordnung-und-rechner-zertifizierungsgebuehren#9n221"
          >
            Rechner für Zertifizierungsgebühren
          </ThemeLink>
        </React.Fragment>
      ),
    },
    {
      title: <React.Fragment>Einsatz und Ausschreibung</React.Fragment>,
      icon: ScrollIcon,
      content: (
        <React.Fragment>
          <ThemeParagraph gutterBottom>
            EN<i>plus</i>-Hackschnitzel definiert sechs verschiedene Sortimente.
            Welches Sortiment kommt für welche Heizung in Frage?
          </ThemeParagraph>
          <ThemeParagraph>
            Der &bdquo;Hackschnitzelrechner&ldquo; berechnet den Wärmepreis,
            damit Angebote besser verglichen und Lieferungen passgenau
            ausgeschrieben werden können.
          </ThemeParagraph>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="einsatz-von-hackschnitzeln"
          >
            Zum Einsatz von Hackschnitzeln inkl. Hackschnitzelrechner
          </ThemeLink>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="ausschreibungsvorlage"
          >
            Zur Ausschreibungsvorlage
          </ThemeLink>
        </React.Fragment>
      ),
    },
    {
      title: <React.Fragment>Hackschnitzelpreis</React.Fragment>,
      icon: EuroIcon,
      content: (
        <React.Fragment>
          <ThemeParagraph>
            Einen Verbraucherpreis für Holzhackschnitzel veröffentlicht das
            Deutsche Pelletinstitut (DEPI) quartalsweise. Dabei werden die
            deutschlandweiten Durchschnittspreise für drei Qualitätsklassen
            (angelehnt an A1, A2 und B) erhoben. Diese bieten größeren Abnehmern
            wie Kommunen eine zuverlässige Basis für vertragliche Vereinbarungen
            mit Händlern und Produzenten.
          </ThemeParagraph>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="heizen-mit-hackschnitzeln/preis"
          >
            Zum Hackschnitzelpreis
          </ThemeLink>
        </React.Fragment>
      ),
    },
    {
      title: (
        <React.Fragment>Förderprogramme für die Heizanlage</React.Fragment>
      ),
      icon: FundingIcon,
      content: (
        <React.Fragment>
          <ThemeParagraph>
            Bund und Länder unterstützen den Ausbau erneuerbarer Energien. Die
            vielfältigen Zuschussvarianten sind jedoch nicht immer einfach zu
            durchschauen. Erfahren Sie hier, welche Programme für Sie in Frage
            kommen.
          </ThemeParagraph>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="foerderprogramme"
          >
            Zu den Förderprogrammen
          </ThemeLink>
          <ThemeLink
            context="hackschnitzel"
            href="[...pageId]"
            as="foerderprogramme#f55is"
          >
            Zum Förderrechner
          </ThemeLink>
        </React.Fragment>
      ),
    },
  ],
};
